import ChevronRight from '@ixigo/icons/ChevronRight';
import Close from '@ixigo/icons/Close';
import Button from '@ixigo/iui/Button';
import ListItem from '@ixigo/iui/ListItem';
import ListItemIcon from '@ixigo/iui/ListItemIcon';
import ListItemText from '@ixigo/iui/ListItemText';
import Typography from '@ixigo/iui/Typography';
import { BottomsheetExtended as Bottomsheet } from '@repo/shared/components';
import { useMemo, useState } from 'react';
import type { BaseCabItemOption, BaseFarePolicyDetail, FareTerm } from '@/api';
import type { IconModule } from '../lazy-icons';
import DynamicIcon from '../lazy-icons/dynamic-icon';
import { NavigationHeader } from '../NavigationHeader';
import { getInterPolatedPolicyInfo } from './utils';
import { getInterPolatedTermDetails } from './utils/policy-interpolation';

// Icon import map for @ixigo/icons package
// Add new icons here as needed by the backend
// TODO: what's the benefit of using this dynamic chunks?
const loadDistanceFilled = () => import('@ixigo/icons/DistanceFilled');
const loadSandTimeFilled = () => import('@ixigo/icons/SandTimeFilled');
const loadCabFilled = () =>
	import('@repo/shared/icons').then((m) => ({ default: m.CabFilled }));

const ICON_IMPORT_MAP: Record<string, () => Promise<IconModule>> = {
	// Fare Policy Icons
	RupeeCircleFilled: () => import('@ixigo/icons/RupeeCircleFilled'),
	DistanceFilled: loadDistanceFilled,
	SandTimeFilled: loadSandTimeFilled,
	CabFilled: loadCabFilled,
	// Aliases for the new backend icon names
	'distance-filled': loadDistanceFilled,
	'waiting-time-filled': loadSandTimeFilled,
	'cab-cancellation-filled': loadCabFilled,
	NoCabFilled: () =>
		import('@repo/shared/icons').then((m) => ({ default: m.NoCabFilled })),
	ParkingFilled: () =>
		import('@repo/shared/icons').then((m) => ({ default: m.ParkingFilled })),
	StateTax: () =>
		import('@repo/shared/icons').then((m) => ({ default: m.StateTax })),
	TollFilled: () =>
		import('@repo/shared/icons').then((m) => ({ default: m.TollFilled })),
	Toll: () => import('@repo/shared/icons').then((m) => ({ default: m.Toll })),
	// Add more icons as needed by your backend team
};

const PolicyItem = ({ policy }: { policy: FareTerm }) => {
	return (
		<ListItem disablePadding className="py-10">
			<ListItemIcon type="logo">
				<div className="flex items-center justify-center bg-primary w-full rounded-10">
					<DynamicIcon iconName={policy.icon} iconImportMap={ICON_IMPORT_MAP} />
				</div>
			</ListItemIcon>
			<ListItemText
				primary={policy.text.primary}
				secondary={policy.text.secondary}
			/>
		</ListItem>
	);
};

export interface FarePoliciesProps extends BaseFarePolicyDetail {
	selectedCab: BaseCabItemOption;
}
export function FarePolicies({
	terms,
	termDetails,
	selectedCab,
}: FarePoliciesProps) {
	const [isOpen, setIsOpen] = useState(false);
	const updatedTerms = useMemo(
		() => getInterPolatedPolicyInfo(terms, selectedCab),
		[terms, selectedCab],
	);
	const updatedTermDetails = useMemo(
		() => getInterPolatedTermDetails(termDetails, selectedCab),
		[termDetails, selectedCab],
	);
	return (
		<div className="flex flex-col gap-y-5 bg-tertiary rounded-[15px] p-15px ">
			<Typography variant="h6" fontWeight="medium">
				Inclusions/Exclusions
			</Typography>
			<ul className="flex flex-col">
				{updatedTerms.map((policy, index) => (
					<PolicyItem key={index} policy={policy} />
				))}
			</ul>

			{termDetails.tncs.length > 0 && (
				<Button
					variant="flat"
					size="md"
					onClick={() => setIsOpen(true)}
					endAdornment={<ChevronRight />}
					className="self-start !px-0"
				>
					Policies
				</Button>
			)}
			<Bottomsheet open={isOpen} onClose={() => setIsOpen(false)}>
				<div className="flex flex-col">
					<NavigationHeader
						title={termDetails.title}
						leftIcon={<Close />}
						leftAction={() => setIsOpen(false)}
						className="sticky top-0 z-[1] w-full px-20 flex-nowrap h-50px bg-primary"
					/>
					<ul className="flex flex-col flex-1 gap-y-5 m-20 mt-10 pl-20 list-disc">
						{updatedTermDetails.tncs.map((tnc, index) => (
							<li key={index} className="text-primary">
								<Typography
									// biome-ignore lint/security/noDangerouslySetInnerHtml: tnc is safe html string
									dangerouslySetInnerHTML={{ __html: tnc }}
									variant="body-sm"
								/>
							</li>
						))}
					</ul>
				</div>
			</Bottomsheet>
		</div>
	);
}

export default FarePolicies;
